import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.ts'
import { canalDePresenca } from '../presence/presence-client.ts'

/**
 * Notificações da pessoa (SPEC-20260924-2111).
 *
 * A fonte é o banco, não o canal ao vivo: o aviso ao vivo só serve para
 * mostrar o cartão na hora e pedir a lista de novo. Quem entra depois de uma
 * queda de conexão — ou no dia seguinte — vê tudo pela consulta.
 */

export type Notificacao = {
  id: string
  /** Hoje só `work_item_assigned`. */
  kind: string
  workItemId: string | null
  /** Código do Item de Trabalho — é por ele que a pessoa reconhece o item. */
  code: string
  titulo: string
  /** Quem causou. */
  autor: string
  criadaEm: string
  lida: boolean
  /** Convite do Calendário: o evento e o dia dele (SPEC-20261027-2350-calendario-real). */
  calendarEventId?: string | null
  data?: string | null
}

export const CHAVE_NOTIFICACOES = ['notifications']

/**
 * A lista + a contagem. Uma consulta só: o painel e o sino leem o mesmo dado,
 * e duas consultas divergiriam no instante entre uma e outra.
 */
export function useNotificacoes(ligado: boolean) {
  const queryClient = useQueryClient()

  /**
   * A PRIMEIRA `abertura` é a conexão inicial, que chega depois da consulta já
   * ter rodado — invalidar ali pediria a mesma coisa duas vezes a cada
   * carregamento. Da segunda em diante é reconexão de verdade, e aí vale:
   * pode ter chegado notificação com o canal caído.
   */
  const primeiraAbertura = useRef(true)
  useEffect(() => {
    if (!ligado) return
    return canalDePresenca.inscrever((evento) => {
      if (evento.tipo === 'abertura') {
        if (primeiraAbertura.current) {
          primeiraAbertura.current = false
          return
        }
        void queryClient.invalidateQueries({ queryKey: CHAVE_NOTIFICACOES })
        return
      }
      if (evento.tipo === 'notificacao') void queryClient.invalidateQueries({ queryKey: CHAVE_NOTIFICACOES })
    })
  }, [ligado, queryClient])

  return useQuery({
    queryKey: CHAVE_NOTIFICACOES,
    enabled: ligado,
    queryFn: async (): Promise<{ itens: Notificacao[]; naoLidas: number }> => (await api.notifications.$get()).json(),
  })
}

/** Marca uma como lida. */
export function useMarcarLida() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await api.notifications.read.$post({ json: { id } })).json(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE_NOTIFICACOES }),
  })
}

export function useMarcarTodasLidas() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => (await api.notifications['read-all'].$post()).json(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE_NOTIFICACOES }),
  })
}

/** Volta para não lida — a tela oferece isso para quem abriu sem poder tratar. */
export function useMarcarNaoLida() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await api.notifications.unread.$post({ json: { id } })).json(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE_NOTIFICACOES }),
  })
}
